import { ICloneable } from '../../interfaces/cloneable.interface';
import { CoordsUtils } from './coords.utils';
import { Point, PointType } from './point';

export type RectType =
  | Rect
  | { x: number; y: number; width: number; height: number };

export class Rect implements ICloneable<Rect> {
  static get empty() {
    return new Rect(0, 0, 0, 0);
  }

  static get infinity() {
    return new Rect(-Infinity, -Infinity, Infinity, Infinity);
  }

  x: number;

  y: number;

  width: number;

  height: number;

  get right() {
    return this.x + this.width;
  }

  set right(value) {
    this.x = value - this.width;
  }

  get bottom() {
    return this.y + this.height;
  }

  set bottom(value) {
    this.y = value - this.height;
  }

  get centerX() {
    return this.x + this.width / 2;
  }

  get centerY() {
    return this.y + this.height / 2;
  }

  constructor(x: number, y: number, width: number, height: number) {
    this.x = x;
    this.y = y;
    this.width = width;
    this.height = height;
  }

  static fromObject(rect: RectType) {
    if (rect instanceof Rect) {
      return rect;
    }

    if (
      !rect ||
      (rect.x === 0 && rect.y === 0 && rect.width === 0 && rect.height === 0)
    ) {
      return Rect.empty;
    } else {
      return new Rect(rect.x, rect.y, rect.width, rect.height);
    }
  }

  static fromElement(element: HTMLElement) {
    const { left, top, width, height } = element.getBoundingClientRect();

    if (left === 0 && top === 0 && width === 0 && height === 0) {
      return Rect.empty;
    } else {
      return new Rect(left, top, width, height);
    }
  }

  static fromWindow(_window: Window) {
    return new Rect(0, 0, _window.innerWidth, _window.innerHeight);
  }

  inflate(inflateBy: number) {
    this.x -= inflateBy;
    this.y -= inflateBy;
    this.right += inflateBy;
    this.bottom += inflateBy;
    return this;
  }

  isInsideOf(rect: Rect) {
    return this.isInsideOfHorizontally(rect) && this.isInsideOfVertically(rect);
  }

  isInsideOfHorizontally(rect: Rect) {
    return rect.x <= this.x && rect.right >= this.right;
  }

  isInsideOfVertically(rect: Rect) {
    return rect.y <= this.y && rect.bottom >= this.bottom;
  }

  intersectsWith(rect: RectType) {
    return (
      rect.x < this.right &&
      this.x < rect.x + rect.width &&
      rect.y < this.bottom &&
      this.y < rect.y + rect.height
    );
  }

  getIntersectionWith(rect: RectType) {
    const x = Math.max(this.x, rect.x);
    const y = Math.max(this.y, rect.y);
    const right = Math.min(this.right, rect.x + rect.width);
    const bottom = Math.min(this.bottom, rect.y + rect.height);

    if (right >= x && bottom >= y) {
      return new Rect(x, y, right - x, bottom - y);
    } else {
      return Rect.empty;
    }
  }

  containsPoint(point: PointType) {
    return CoordsUtils.isPointInsideRect(point, this);
  }

  isPointNearBoundaries(point: PointType, threshold: number = 0) {
    return CoordsUtils.isPointNearRectBoundaries(point, this, threshold);
  }

  isBiggerThan(rect: RectType) {
    return CoordsUtils.isRectBiggerThan(this, rect);
  }

  isSomeAxisBiggerThan(rect: RectType) {
    return CoordsUtils.isRectSomeAxisBiggerThan(this, rect);
  }

  movePosTo(point: PointType) {
    this.x = point.x;
    this.y = point.y;
    return this;
  }

  moveCenterTo(point: PointType) {
    this.x = point.x - this.width / 2;
    this.y = point.y - this.height / 2;
    return this;
  }

  relativeTo(point: PointType) {
    this.x -= point.x;
    this.y -= point.y;
    return this;
  }

  pos() {
    return new Point(this.x, this.y);
  }

  center() {
    return new Point(this.centerX, this.centerY);
  }

  clone() {
    return new Rect(this.x, this.y, this.width, this.height);
  }

  equals(rect: RectType) {
    if (!rect || typeof rect !== 'object') {
      return false;
    }

    if (rect === this) {
      return true;
    }

    return (
      this.x === rect.x &&
      this.y === rect.y &&
      this.width === rect.width &&
      this.height === rect.height
    );
  }
}
